import React from 'react';
import groupBy from 'lodash/groupBy';
import { ProviderFilterPageMeta as Meta } from 'components/Meta';
import type { ProvidersRouteParams } from 'types';
import { buildURL, getPageTitle, getPageDescription, encode } from './utils';
import ProviderPageBreadcrumbs from './breadcrumbs';
import { BaseCardTile, Link, PageHero } from 'styled';
import AlphabetNav from 'styled/components/AlphabetNav';
import Divider from '@mui/material/Divider';
import ScrollableAnchor, { goToTop } from 'react-scrollable-anchor';
import { PageHero as IPageHero } from 'types';
import sanitizeImage from 'state/sanitizers/sanitizeImage';

interface Props {
  routeParams: ProvidersRouteParams;
  items: string[];
  itemDisplay?: (s: string) => string;
}

// Bit of a hack so we can use the PageHero module without sanity
const fakeHero = {
  variant: 'general-md',
  color: 'PALE_BLUE',
  buttonLink: '',
  buttonLabel: '',
  buttonVariant: '',
  modules: [],
  mobileImage: {},
  desktopImage: sanitizeImage({
    _id: 'image-35038b5fd785c24c49552cc448e7bec233a1fc63-243x248-svg',
    url: 'https://cdn.sanity.io/images/0b678gck/buoy-public-site/35038b5fd785c24c49552cc448e7bec233a1fc63-243x248.svg?w=2000&fit=max&auto=format&dpr=2',
  }),
  desktopHeroImageWidth: 243,
};

const ProvidersFilterPageView: React.FC<Props> = ({
  routeParams,
  items,
  itemDisplay,
}) => {
  const groupedContent = groupBy(items, (s) => s.charAt(0).toUpperCase());
  Object.keys(groupedContent).forEach((key) => groupedContent[key].sort());
  const sortedKeys = Object.keys(groupedContent).sort();
  const title = getPageTitle(routeParams);
  const description = getPageDescription(routeParams);
  const pathname = buildURL(routeParams);
  return (
    <div>
      <Meta title={title} description={description} pathname={pathname} />
      <ProviderPageBreadcrumbs rp={routeParams} />
      <div className="[&_img]:mb-6 [&_img]:mr-8 [&_img]:max-h-[100px] [&_img]:self-end sm:[&_img]:m-0 sm:[&_img]:max-h-[275px] sm:[&_img]:w-[50vw] sm:[&_img]:self-center md:[&_img]:max-h-[350px]">
        <PageHero
          hero={
            {
              ...fakeHero,
              title,
              description,
            } as unknown as IPageHero
          }
        />
      </div>
      <AlphabetNav enabledLetters={sortedKeys} />
      <Divider className="sm:mb-4" />
      <div className="mx-auto my-0 flex max-w-[81.25rem] flex-col p-6 md:p-9">
        {sortedKeys.map((group, i) => (
          <ScrollableAnchor key={group} id={group}>
            <div className="py-8">
              <h2 className="mb-4 flex w-full justify-between align-baseline text-sm md:font-sans md:text-[2rem]">
                <span>{group}</span>
                {i !== 0 && (
                  <Link
                    onClick={goToTop}
                    variant="underline-blue"
                    ariaLabel="Scroll to top of page"
                    label="Back to top"
                    className="!text-base"
                  />
                )}
              </h2>
              <ul className="grid w-full grid-cols-1 gap-x-2 gap-y-8 sm:grid-cols-3">
                {groupedContent[group].map((item: any) => (
                  <li className="flex" key={item}>
                    <BaseCardTile
                      title={itemDisplay ? itemDisplay(item) : item}
                      url={`${pathname}/${encode(item)}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </ScrollableAnchor>
        ))}
      </div>
    </div>
  );
};

export default ProvidersFilterPageView;
